import { FeatureFlag } from 'types/features';
import { InvoiceData, InvoiceStatusType, InvoiceType } from 'types/invoices';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

export const MANAGE_INVOICE_PAYERS_PERMISSION = 'UPDATE.INVOICES.MANAGE_PAYERS';

const PAYER_EDITING_STATUSES: string[] = [
    InvoiceStatusType.DRAFT,
    InvoiceStatusType.REVIEWED,
    InvoiceStatusType.APPROVED,
];

type SharedPayment = {
    /** The shared-payment feature is on; everything below is false while it is off. */
    isEnabled: boolean;
    /** A FINALIZED-or-later copy that carries one payer's share of a split invoice. */
    isSharedPaymentCopy: boolean;
    /** A shared-payment copy or a credit note of one: its amounts are a share of the group's, not its own. */
    hasPaymentShare: boolean;
    /**
     * Totals read as the receipt reads them, the whole invoice and this payer's share: a copy, or a credit note of one,
     * which carries the copy's share and full amounts, negated, without being a copy itself.
     */
    hasShareTotals: boolean;
    /** A debit invoice before finalization whose payer list is shown and, with the permission, edited. */
    hasPayerList: boolean;
    /** More than one payer before the split, which is what blocks prepayments until the invoice is split. */
    hasMultiplePayers: boolean;
};

export function useSharedPayment(invoice: InvoiceData | undefined): SharedPayment {
    const isEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_SHARED_PAYMENT_FUNCTIONALITY);
    if (!invoice || !isEnabled) {
        return {
            isEnabled: false,
            isSharedPaymentCopy: false,
            hasPaymentShare: false,
            hasShareTotals: false,
            hasPayerList: false,
            hasMultiplePayers: false,
        };
    }
    const hasPayerList =
        invoice.invoiceTypeCode === InvoiceType.DEBIT &&
        !invoice.isSplit &&
        PAYER_EDITING_STATUSES.includes(invoice.invoiceStatusTypeCode);
    const isSharedPaymentCopy = !!invoice.isSharedPayment;
    const hasPaymentShare = invoice.paymentShareRate !== null && invoice.paymentShareRate !== undefined;
    return {
        isEnabled,
        isSharedPaymentCopy,
        hasPaymentShare,
        hasShareTotals: isSharedPaymentCopy || (hasPaymentShare && !!invoice.preSplitTotals),
        hasPayerList,
        hasMultiplePayers: hasPayerList && (invoice.payers?.length ?? 0) > 1,
    };
}
